import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Empty, Form, Input, InputNumber, Modal, Select, Skeleton,
  Space, Statistic, Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import type {
  CreateRefundRequest, RefundMethod, RefundRow, RefundableReceipt,
} from '@velora/contracts';
import { ApiError, productApi } from '../../api';

const METHODS: RefundMethod[] = ['cash', 'upi', 'card', 'bank_transfer', 'cheque'];

function paise(n: number): string { return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 }); }
function extractErrMsg(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) return (err.body as { message?: string } | null)?.message ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

/**
 * Accounts desk — Refunds.
 * M-02: every refund carries a non-blank reason AND a non-blank approver.
 * Both invariants live in the schema (CHECK), the contract (Zod min), and here.
 */
export function RefundsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [seed, setSeed] = useState<RefundableReceipt | null>(null);
  const list = useQuery({
    queryKey: ['refunds', institution],
    queryFn: () => productApi.listRefunds(institution),
    enabled: !!institution,
  });
  if (list.isLoading || !list.data) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Refunds</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            M-02 · every refund needs a non-blank reason and a named approver. Both are stored, both are audited.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setSeed(null); setAddOpen(true); }}>
          New refund
        </Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        <Card size="small"><Statistic title="Refunds recorded" value={list.data.totals.count} /></Card>
        <Card size="small"><Statistic title="Total refunded"
          value={`₹${paise(list.data.totals.refunded_paise)}`}
          valueStyle={{ color: '#a24040' }} /></Card>
      </div>

      <Card size="small">
        {list.data.rows.length === 0
          ? <Empty description="No refunds recorded." style={{ padding: 20 }} />
          : (
            <Table<RefundRow>
              size="small" pagination={{ pageSize: 25 }} rowKey="id"
              dataSource={list.data.rows}
              columns={[
                { title: 'When', dataIndex: 'at', key: 'at', width: 150,
                  render: (v: string) => new Date(v).toLocaleString() },
                { title: 'Receipt', dataIndex: 'receipt_number', key: 'rc', width: 150,
                  render: (v: string) => <Tag style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</Tag> },
                { title: 'Student', key: 's',
                  render: (_, r) => <>
                    <b>{r.student_name}</b>
                    <div style={{ fontSize: 11, color: '#8a94a6', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.admission_number}</div>
                  </> },
                { title: 'Head', dataIndex: 'fee_head_name', key: 'h' },
                { title: 'Amount', dataIndex: 'amount_paise', key: 'a', align: 'right', width: 120,
                  render: (v: number) => <b>₹{paise(v)}</b> },
                { title: 'Method', dataIndex: 'method', key: 'm', width: 110,
                  render: (v: string) => <Tag>{v}</Tag> },
                { title: 'Approver', dataIndex: 'approver', key: 'appr' },
                { title: 'Reason', dataIndex: 'reason', key: 'rz',
                  render: (v: string) => <span style={{ fontSize: 12.5 }}>{v}</span> },
              ]}
            />
          )}
      </Card>

      <RefundModal open={addOpen} institution={institution}
        seed={seed}
        onSeed={setSeed}
        onClose={() => setAddOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['refunds', institution] })} />
    </Space>
  );
}

interface RefundForm {
  amount_rupees: number;
  method: RefundMethod;
  refunded_on: Dayjs;
  reason: string;
  approver: string;
  note?: string;
}

function RefundModal({
  open, institution, seed, onSeed, onClose, onDone,
}: {
  open: boolean; institution: string;
  seed: RefundableReceipt | null;
  onSeed: (r: RefundableReceipt | null) => void;
  onClose: () => void; onDone: () => void;
}) {
  const [form] = Form.useForm<RefundForm>();
  const [search, setSearch] = useState('');
  const refundable = useQuery({
    queryKey: ['refundable', institution, search],
    queryFn: () => productApi.listRefundableReceipts(institution, search || undefined),
    enabled: open && !!institution,
  });
  const create = useMutation({
    mutationFn: (input: CreateRefundRequest) => productApi.createRefund(institution, input),
    onSuccess: () => {
      form.resetFields(); onSeed(null);
      onDone(); onClose();
      void message.success('Refund recorded');
    },
  });
  const err = extractErrMsg(create.error);

  const options = useMemo(
    () => (refundable.data?.rows ?? []).map((r) => ({
      value: r.receipt_id,
      label: `${r.receipt_number} · ${r.student_name} (${r.admission_number}) · ₹${paise(r.refundable_paise)} refundable`,
      row: r,
    })),
    [refundable.data],
  );

  return (
    <Modal open={open} title="New refund" onCancel={() => { create.reset(); onClose(); }}
      okText="Record refund" confirmLoading={create.isPending}
      onOk={() => form.submit()} destroyOnClose width={640}>
      <Alert type="warning" showIcon
        message="M-02 · reason and approver are both required"
        description="Refunds without a written reason and a named approver are refused at the server. This keeps the paper trail honest."
        style={{ marginBottom: 12 }} />

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Pick a receipt</div>
        <Select showSearch filterOption={false} onSearch={setSearch}
          placeholder="Search by receipt number, student name, admission number"
          style={{ width: '100%', marginTop: 6 }}
          value={seed?.receipt_id}
          onChange={(v, opt) => {
            const picked = (Array.isArray(opt) ? opt[0] : opt) as { row?: RefundableReceipt } | undefined;
            if (picked?.row) {
              onSeed(picked.row);
              form.setFieldsValue({
                method: picked.row.method,
                amount_rupees: picked.row.refundable_paise / 100,
              });
            } else if (typeof v === 'string') {
              const found = options.find((o) => o.value === v);
              if (found) {
                onSeed(found.row);
                form.setFieldsValue({
                  method: found.row.method,
                  amount_rupees: found.row.refundable_paise / 100,
                });
              }
            }
          }}
          suffixIcon={<SearchOutlined />}
          options={options}
        />
        {seed && (
          <div style={{ marginTop: 8, padding: 10, background: '#f7f7f5', borderRadius: 6, fontSize: 12.5 }}>
            <b>{seed.receipt_number}</b> · {seed.student_name} · {seed.fee_head_name}
            <div style={{ color: '#5b6478' }}>
              Original ₹{paise(seed.amount_paise)}
              {' · already refunded '}₹{paise(seed.refunded_paise)}
              {' · refundable up to '}<b>₹{paise(seed.refundable_paise)}</b>
            </div>
          </div>
        )}
      </div>

      <Form<RefundForm> form={form} layout="vertical"
        onFinish={(v) => {
          if (!seed) { void message.error('Pick a receipt'); return; }
          const paiseAmt = Math.round(v.amount_rupees * 100);
          if (paiseAmt > seed.refundable_paise) {
            void message.error(`Only ₹${paise(seed.refundable_paise)} refundable on this receipt.`);
            return;
          }
          create.mutate({
            receipt_id: seed.receipt_id,
            amount_paise: paiseAmt,
            method: v.method,
            refunded_on: v.refunded_on.format('YYYY-MM-DD'),
            reason: v.reason.trim(),
            approver: v.approver.trim(),
            note: v.note?.trim() || undefined,
          });
        }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="amount_rupees" label="Amount (₹)"
            rules={[{ required: true, type: 'number', min: 1 }]}>
            <InputNumber min={1} style={{ width: '100%' }} disabled={!seed} />
          </Form.Item>
          <Form.Item name="method" label="Method" rules={[{ required: true }]}>
            <Select options={METHODS.map((m) => ({ value: m, label: m.replace(/_/g, ' ') }))} disabled={!seed} />
          </Form.Item>
        </div>
        <Form.Item name="refunded_on" label="Refunded on" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} disabled={!seed} />
        </Form.Item>
        <Form.Item name="reason" label="Reason (M-02)"
          rules={[{ required: true, min: 5, max: 500, whitespace: true }]}>
          <Input.TextArea rows={3} disabled={!seed}
            placeholder="Why is this refund happening? At least a full sentence — not 'as requested'." />
        </Form.Item>
        <Form.Item name="approver" label="Approver (M-02)"
          rules={[{ required: true, min: 1, max: 160, whitespace: true }]}>
          <Input disabled={!seed} placeholder="Named principal / owner. Not a role, a person." />
        </Form.Item>
        <Form.Item name="note" label="Internal note (optional)">
          <Input.TextArea rows={2} disabled={!seed} />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}
